import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  type?: 'website' | 'article' | 'profile';
  author?: string;
}

const defaultSEO = {
  siteName: 'Terravia Ceramics',
  title: 'Terravia Ceramics - Contemporary Pottery & Ceramic Art by Umar Abdulrasheed',
  description: 'Contemporary pottery and ceramic art shaped by earth and fire. Terravia Ceramics creates handcrafted sculptural vessels and ceramic works exploring the dialogue between tradition and innovation.',
  keywords: 'Terravia Ceramics, Umar Abdulrasheed, contemporary ceramics, ceramic artist, pottery, handmade pottery, sculptural ceramics, stoneware, ceramic sculpture, British ceramics, Nigerian artist, London ceramics, clay art, ceramic vessels, art pottery, ceramic exhibition',
  image: 'https://terravia-clay-canvas.lovable.app/og-image.jpg',
  url: 'https://terravia-clay-canvas.lovable.app',
  author: 'Umar Abdulrasheed',
};

const pageSEO: Record<string, { title: string; description: string; keywords: string }> = {
  '/': {
    title: 'Terravia Ceramics - Contemporary Pottery & Ceramic Art',
    description: 'Contemporary pottery and ceramic art shaped by earth and fire. Terravia Ceramics by Umar Abdulrasheed creates handcrafted sculptural vessels exploring tradition and innovation.',
    keywords: 'Terravia Ceramics, contemporary pottery, ceramic art, handmade ceramics, Umar Abdulrasheed, sculptural vessels, British ceramics',
  },
  '/portfolio': {
    title: 'Portfolio | Terravia Ceramics - Ceramic Artworks Collection',
    description: 'Explore the portfolio of Terravia Ceramics featuring sculptural ceramic works including Paired Silence, Emergent Form, Quiet Alignment, and more handcrafted pieces.',
    keywords: 'ceramic portfolio, sculptural ceramics, Paired Silence, Emergent Form, ceramic vessels, stoneware art, handbuilt ceramics, Terravia collection',
  },
  '/about': {
    title: 'About Umar Abdulrasheed | Terravia Ceramics Artist',
    description: 'Learn about Umar Abdulrasheed, Nigerian-born ceramic sculptor based in London. Discover his artistic journey, philosophy, and the story behind Terravia Ceramics.',
    keywords: 'Umar Abdulrasheed, ceramic artist biography, Nigerian ceramicist, London pottery artist, ceramic sculptor, artist statement, Terravia founder',
  },
  '/exhibitions': {
    title: 'Exhibitions | Terravia Ceramics - Gallery Shows & Events',
    description: 'View past and current exhibitions featuring Terravia Ceramics including Soul in Motion at Espacio Gallery London, Earthen Echoes, and international shows.',
    keywords: 'ceramic exhibitions, Soul in Motion, Espacio Gallery, Earthen Echoes, art exhibitions London, pottery shows, gallery exhibitions, ceramic art events',
  },
  '/news': {
    title: 'News & Press | Terravia Ceramics - Latest Updates',
    description: 'Read the latest news, press coverage, and updates about Terravia Ceramics and artist Umar Abdulrasheed including exhibition announcements and features.',
    keywords: 'Terravia news, ceramic artist press, pottery news, art features, exhibition announcements, Umar Abdulrasheed news',
  },
  '/contact': {
    title: 'Contact | Terravia Ceramics - Get in Touch',
    description: 'Contact Terravia Ceramics for exhibition enquiries, commissions, and collaborations. Studios in London UK and Ilorin Nigeria. Studio visits by appointment.',
    keywords: 'contact Terravia, ceramic commissions, exhibition enquiries, London studio, Nigeria studio, pottery collaboration',
  },
  '/artist-cv': {
    title: 'Artist CV | Umar Abdulrasheed - Terravia Ceramics',
    description: 'Professional CV of Umar Abdulrasheed featuring education, exhibitions, awards, and career achievements in contemporary ceramic art.',
    keywords: 'artist CV, Umar Abdulrasheed resume, ceramic artist credentials, exhibition history, art awards, professional ceramicist',
  },
};

export const SEOHead = ({ 
  title, 
  description, 
  keywords,
  image,
  type = 'website',
  author 
}: SEOHeadProps) => {
  const location = useLocation();
  const currentPath = location.pathname;
  
  const pageData = pageSEO[currentPath] || pageSEO['/'];
  
  const finalTitle = title || pageData.title;
  const finalDescription = description || pageData.description;
  const finalKeywords = keywords || pageData.keywords;
  const finalImage = image || defaultSEO.image;
  const finalAuthor = author || defaultSEO.author;
  const canonicalUrl = `${defaultSEO.url}${currentPath === '/' ? '' : currentPath}`;

  useEffect(() => {
    // Update document title
    document.title = finalTitle;

    // Update or create meta tags
    const updateMetaTag = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attr}="${name}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attr, name);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    // Basic meta tags
    updateMetaTag('description', finalDescription);
    updateMetaTag('keywords', finalKeywords);
    updateMetaTag('author', finalAuthor);

    // Open Graph tags
    updateMetaTag('og:title', finalTitle, true);
    updateMetaTag('og:description', finalDescription, true);
    updateMetaTag('og:type', type, true);
    updateMetaTag('og:url', canonicalUrl, true);
    updateMetaTag('og:image', finalImage, true);
    updateMetaTag('og:site_name', defaultSEO.siteName, true);

    // Twitter Card tags
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:title', finalTitle);
    updateMetaTag('twitter:description', finalDescription);
    updateMetaTag('twitter:image', finalImage);

    // Update canonical link
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', canonicalUrl);

  }, [finalTitle, finalDescription, finalKeywords, finalImage, finalAuthor, type, canonicalUrl]);

  return null;
};

export default SEOHead;
